import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import {
  Plane,
  Volume2,
  Download,
  Check,
  Shield,
  Loader2,
  Sparkles,
} from "lucide-react";
import { track } from "@/lib/analytics";
import { useCanonical } from "@/lib/useCanonical";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUDIO_SRC = "/audio/aeroporto-cdg-guia.mp3";

const COPY = {
  pt: {
    badge: "Guia grátis · pré-viagem a Paris",
    title1: "As 10 frases que salvam sua chegada",
    title2: "no aeroporto Charles de Gaulle.",
    subtitle:
      "Baixe o áudio (837 KB · voz do Prof Lionel, nativo de Paris) + o card pra imprimir. Zero curso, zero enrolação. Chega em Paris destravado.",
    listenBefore: "Escute os primeiros 30 segundos aqui:",
    formTitle: "Onde te envio o guia?",
    nameLabel: "Nome",
    namePh: "Como te chamo?",
    emailLabel: "E-mail",
    emailPh: "seu@email.com",
    whatsappLabel: "WhatsApp (opcional)",
    whatsappPh: "com DDD, ex: 21998765432",
    dateLabel: "Quando você vai?",
    datePh: "Quando você embarca? (opcional)",
    consent:
      "Concordo em receber o guia + 3 dicas semanais de francês. Cancelo quando quiser.",
    submit: "Quero o guia gratuito",
    submitting: "Enviando...",
    success: "🎉 Guia enviado! Confere sua caixa de entrada.",
    error: "Deu ruim. Tenta de novo?",
    downloadTitle: "Pronto! Já pode baixar tudo:",
    downloadAudio: "Baixar áudio (837 KB)",
    downloadCard: "Baixar card imprimível",
    proofTitle: "Alunos que já usaram",
    proofs: [
      { name: "Beatriz, 32 · foi em março", text: "Cheguei no CDG com o card no bolso. Passei pela imigração em 2 minutos falando francês. Sem drama, sem inglês de emergência." },
      { name: "Ricardo, 45 · foi em janeiro", text: "Baixei, escutei no avião 3 vezes, aterrizei confiante. O taxista me elogiou o « bonjour ». Vale ouro." },
    ],
    ctaLater: "Depois de destravar a chegada, vale reservar a 1ª aula grátis →",
    faqTitle: "Perguntas rápidas",
    faqs: [
      { q: "Custa mesmo nada?", a: "Nada. Sem cartão, sem trial. Você baixa o áudio e o card imprimível ao terminar o formulário." },
      { q: "Vou receber spam?", a: "Não. 3 dicas semanais de francês + convite pra 1ª aula grátis. Cancela quando quiser em 1 clique." },
      { q: "Preciso saber francês pra usar?", a: "Não. As frases são fonéticas e explicadas. Nível zero → sobrevive em CDG." },
    ],
    footer: "Todo o conteúdo é gerado pelo Prof Lionel, natif de Paris, há 20 anos ensinando brasileiros.",
  },
  en: {
    badge: "Free guide · pre-Paris trip",
    title1: "The 10 phrases that save your arrival",
    title2: "at Charles de Gaulle airport.",
    subtitle:
      "Download the audio (837 KB · Prof Lionel's real voice, Paris native) + a printable card. Zero course, zero fluff. Land in Paris unblocked.",
    listenBefore: "Listen to the first 30 seconds here:",
    formTitle: "Where should I send the guide?",
    nameLabel: "Name",
    namePh: "How should I call you?",
    emailLabel: "Email",
    emailPh: "you@email.com",
    whatsappLabel: "WhatsApp (optional)",
    whatsappPh: "with country code",
    dateLabel: "When are you flying?",
    datePh: "Departure date (optional)",
    consent:
      "I agree to receive the guide + 3 weekly French tips. I can unsubscribe anytime.",
    submit: "Send me the guide",
    submitting: "Sending...",
    success: "🎉 Guide sent! Check your inbox.",
    error: "Something went wrong. Try again?",
    downloadTitle: "Done! Download it all:",
    downloadAudio: "Download audio (837 KB)",
    downloadCard: "Download printable card",
    proofTitle: "Travelers who already used it",
    proofs: [
      { name: "Beatriz, 32 · went in March", text: "Cleared CDG in 2 minutes speaking French. No drama, no emergency English." },
      { name: "Ricardo, 45 · went in January", text: "Listened on the plane 3× and landed confident. The taxi driver complimented my « bonjour »." },
    ],
    ctaLater: "After surviving arrival, book your 1st free class →",
    faqTitle: "Quick FAQ",
    faqs: [
      { q: "Really free?", a: "Free. No card, no trial. Audio + card unlock after signup." },
      { q: "Will I get spam?", a: "No. 3 weekly French tips + one invite to a free class. Unsubscribe in 1 click." },
      { q: "Need to speak French to use it?", a: "No. Phrases are phonetic + explained. Zero level → CDG survival." },
    ],
    footer: "All content by Prof Lionel, native Parisian teaching Brazilians for 20 years.",
  },
  fr: {
    badge: "Guide offert · pré-voyage à Paris",
    title1: "Les 10 phrases qui sauvent ton arrivée",
    title2: "à l'aéroport Charles de Gaulle.",
    subtitle:
      "Télécharge l'audio (837 Ko · vraie voix de Prof Lionel, natif de Paris) + une carte imprimable. Zéro cours, zéro blabla. Atterris débloqué.",
    listenBefore: "Écoute les 30 premières secondes ici :",
    formTitle: "Où t'envoyer le guide ?",
    nameLabel: "Prénom",
    namePh: "Comment t'appelles-tu ?",
    emailLabel: "E-mail",
    emailPh: "toi@email.com",
    whatsappLabel: "WhatsApp (facultatif)",
    whatsappPh: "avec indicatif pays",
    dateLabel: "Tu pars quand ?",
    datePh: "Date de départ (facultatif)",
    consent:
      "J'accepte de recevoir le guide + 3 astuces hebdomadaires. Je peux me désinscrire.",
    submit: "Envoie-moi le guide",
    submitting: "Envoi...",
    success: "🎉 Guide envoyé ! Vérifie ta boîte mail.",
    error: "Ça a bloqué. Essaie encore ?",
    downloadTitle: "Fait ! Télécharge tout :",
    downloadAudio: "Télécharger audio (837 Ko)",
    downloadCard: "Télécharger la carte imprimable",
    proofTitle: "Voyageurs qui l'ont déjà utilisé",
    proofs: [
      { name: "Beatriz, 32 · voyage en mars", text: "Passé la douane en 2 min en parlant français. Aucun drame." },
      { name: "Ricardo, 45 · voyage en janvier", text: "Écouté 3× dans l'avion, atterri confiant. Le chauffeur m'a complimenté mon « bonjour »." },
    ],
    ctaLater: "Après avoir débloqué ton arrivée, réserve ton 1er cours offert →",
    faqTitle: "FAQ rapide",
    faqs: [
      { q: "Vraiment gratuit ?", a: "Gratuit. Sans carte, sans essai. Audio + carte débloqués après inscription." },
      { q: "Vais-je recevoir du spam ?", a: "Non. 3 astuces hebdo + 1 invitation au cours offert. Désinscription en 1 clic." },
      { q: "Faut-il savoir français ?", a: "Non. Les phrases sont phonétiques et expliquées." },
    ],
    footer: "Contenu créé par Prof Lionel, parisien natif enseignant aux Brésiliens depuis 20 ans.",
  },
};

const ParisEm10Frases = () => {
  useCanonical();
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.pt;

  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    date: "",
    consent: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title =
      lang === "fr"
        ? "10 phrases pour Charles de Gaulle · Guide offert"
        : lang === "en"
        ? "10 phrases for Charles de Gaulle · Free guide"
        : "10 frases para o CDG · Guia grátis Prof Lionel";
    track("landing_view", { variant: "paris_em_10_frases", language: lang });
  }, [lang]);

  // JSON-LD injection — Breadcrumb + FAQPage + Speakable. Big AI-Overview win
  // since this LP already ships 3 curated Q&As per language.
  useEffect(() => {
    const url = "https://npdlbr.com.br/paris-em-10-frases";
    const pageLabel =
      lang === "fr"
        ? "10 phrases · aéroport CDG"
        : lang === "en"
        ? "10 phrases · CDG airport"
        : "10 frases · aeroporto CDG";

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: "https://npdlbr.com.br/" },
        { "@type": "ListItem", position: 2, name: pageLabel, item: url },
      ],
    };
    const faqPage = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: t.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const webPage = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": url,
      url,
      name: document.title,
      inLanguage: lang,
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".lp-hero-title", ".lp-hero-subtitle", ".lp-faq-q"],
      },
    };

    const emit = (obj, tag) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.dynamic = tag;
      el.textContent = JSON.stringify(obj);
      document.head.appendChild(el);
      return el;
    };
    const els = [
      emit(breadcrumb, "paris-breadcrumb"),
      emit(faqPage, "paris-faq"),
      emit(webPage, "paris-webpage"),
    ];
    return () => els.forEach((el) => el.remove());
  }, [lang, t.faqs]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(lang === "fr" ? "Nom et e-mail requis." : "Nome e e-mail obrigatórios.");
      return;
    }
    if (!form.consent) {
      toast.error(lang === "fr" ? "Consentement requis." : "Consentimento é obrigatório.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        whatsapp: form.whatsapp.trim() || null,
        source: "paris_em_10_frases",
        interest: "viagem,pronuncia",
        message: form.date ? `Viagem: ${form.date}` : null,
        language: lang,
      });
      setSubmitted(true);
      toast.success(t.success);
      track("lead_created", {
        source: "paris_em_10_frases",
        language: lang,
      });
    } catch (err) {
      toast.error(t.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="paris-em-10-frases-page"
      className="bg-[#f4f1ea] text-[#1e2a5a]"
    >
      <main className="max-w-4xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        {/* Header */}
        <div className="inline-flex items-center gap-2 rounded-full border border-[#d9381e] px-3.5 py-1.5 text-xs text-[#d9381e] uppercase tracking-[0.18em]">
          <Plane className="h-3.5 w-3.5" strokeWidth={2} />
          {t.badge}
        </div>
        <h1
          data-testid="paris-hero-title"
          className="lp-hero-title font-display mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
        >
          {t.title1}
          <br />
          <span className="text-[#d9381e]">{t.title2}</span>
        </h1>
        <p className="lp-hero-subtitle mt-5 text-base sm:text-lg text-[#5c5c5c] leading-relaxed max-w-2xl">
          {t.subtitle}
        </p>

        {/* Audio preview */}
        <div className="mt-10 rounded-xl border border-[#1e2a5a] bg-[#1e2a5a] text-[#f4f1ea] p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#d9381e] flex items-center justify-center">
              <Volume2 className="h-5 w-5" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.18em] text-[#e5b3b3]">
                🎙️ {t.listenBefore}
              </p>
              <audio
                controls
                preload="metadata"
                src={AUDIO_SRC}
                className="mt-3 w-full"
                data-testid="paris-audio-preview"
                onPlay={() =>
                  track("paris_10_audio_play", { language: lang })
                }
              />
            </div>
          </div>
        </div>

        {/* Form or download area */}
        {!submitted ? (
          <form
            onSubmit={submit}
            data-testid="paris-form"
            className="mt-10 bg-white border border-[#e5e3db] rounded-xl p-6 sm:p-8 shadow-[0_20px_60px_-30px_rgba(30,42,90,0.35)]"
          >
            <p className="font-display text-xl sm:text-2xl">{t.formTitle}</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <FieldInput
                label={t.nameLabel}
                placeholder={t.namePh}
                value={form.name}
                onChange={(v) => set("name", v)}
                testid="paris-name"
                required
              />
              <FieldInput
                label={t.emailLabel}
                placeholder={t.emailPh}
                value={form.email}
                onChange={(v) => set("email", v)}
                testid="paris-email"
                type="email"
                required
              />
              <FieldInput
                label={t.whatsappLabel}
                placeholder={t.whatsappPh}
                value={form.whatsapp}
                onChange={(v) => set("whatsapp", v)}
                testid="paris-whatsapp"
              />
              <FieldInput
                label={t.dateLabel}
                placeholder={t.datePh}
                value={form.date}
                onChange={(v) => set("date", v)}
                testid="paris-date"
                type="date"
              />
            </div>
            <label className="mt-5 flex items-start gap-2 text-xs text-[#5c5c5c] cursor-pointer">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)}
                data-testid="paris-consent"
                className="mt-0.5 h-4 w-4 accent-[#d9381e]"
              />
              <span>{t.consent}</span>
            </label>
            <button
              type="submit"
              disabled={submitting}
              data-testid="paris-submit"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#d9381e] px-6 py-3 text-sm font-semibold text-[#f4f1ea] hover:bg-[#1e2a5a] transition-colors disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {submitting ? t.submitting : t.submit}
            </button>
            <p className="mt-4 flex items-center gap-2 text-[11px] text-[#5c5c5c]">
              <Shield className="h-3 w-3" strokeWidth={2} />
              {t.footer}
            </p>
          </form>
        ) : (
          <div
            data-testid="paris-success"
            className="mt-10 rounded-xl border-2 border-[#1e7a3e] bg-[#e8f4ea] p-6 sm:p-8"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1e7a3e] flex items-center justify-center">
                <Check className="h-5 w-5 text-white" strokeWidth={2.5} />
              </div>
              <p className="font-display text-xl text-[#1e2a5a]">
                {t.downloadTitle}
              </p>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <a
                href={AUDIO_SRC}
                download="10-frases-cdg-prof-lionel.mp3"
                data-testid="paris-download-audio"
                onClick={() =>
                  track("paris_10_download", { asset: "audio", language: lang })
                }
                className="inline-flex items-center gap-2 rounded-full bg-[#d9381e] px-5 py-2.5 text-sm font-semibold text-[#f4f1ea] hover:bg-[#1e2a5a] transition-colors"
              >
                <Download className="h-4 w-4" />
                {t.downloadAudio}
              </a>
              <a
                href="/blog/10-frases-aeroporto-charles-de-gaulle#card"
                data-testid="paris-download-card"
                onClick={() =>
                  track("paris_10_download", { asset: "card", language: lang })
                }
                className="inline-flex items-center gap-2 rounded-full border-2 border-[#1e2a5a] px-5 py-2.5 text-sm font-semibold text-[#1e2a5a] hover:bg-[#1e2a5a] hover:text-[#f4f1ea] transition-colors"
              >
                <Download className="h-4 w-4" />
                {t.downloadCard}
              </a>
            </div>
            <a
              href="/aula-gratis?utm_source=paris_10&utm_medium=post_signup"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#d9381e] hover:text-[#1e2a5a] link-underline"
              data-testid="paris-cta-aula-gratis"
            >
              <Sparkles className="h-4 w-4" />
              {t.ctaLater}
            </a>
          </div>
        )}

        {/* Proofs */}
        <section className="mt-16">
          <p className="overline">{t.proofTitle}</p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {t.proofs.map((p) => (
              <blockquote
                key={p.name}
                className="bg-white border border-[#e5e3db] rounded-xl p-5"
              >
                <p className="text-sm sm:text-base text-[#3a3a3a] leading-relaxed">
                  « {p.text} »
                </p>
                <p className="mt-3 text-xs uppercase tracking-[0.15em] text-[#5c5c5c]">
                  {p.name}
                </p>
              </blockquote>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-16">
          <p className="overline">{t.faqTitle}</p>
          <div className="mt-5 space-y-3">
            {t.faqs.map((f) => (
              <details
                key={f.q}
                className="bg-white border border-[#e5e3db] rounded-xl p-5 group"
              >
                <summary className="lp-faq-q cursor-pointer font-medium text-[#1e2a5a] flex items-center justify-between">
                  {f.q}
                  <span className="text-[#d9381e] group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-[#5c5c5c] leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

const FieldInput = ({ label, placeholder, value, onChange, testid, type = "text", required }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.15em] text-[#5c5c5c]">
      {label}
      {required && " *"}
    </span>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      data-testid={testid}
      className="mt-1 w-full rounded-lg border border-[#e5e3db] px-4 py-2.5 text-sm bg-white focus:border-[#d9381e] focus:outline-none focus:ring-2 focus:ring-[#d9381e]/15"
    />
  </label>
);

export default ParisEm10Frases;
